import { synergyDefinitionOf } from "../data/moduleSynergyDefinitions.js";
import { evaluateModuleEffectStats } from "../modules/ModuleEffect.js";
import { t } from "../i18n/i18n.js";
import type { ModalHost } from "./ModalHost.js";
import type { SynergyInfoData } from "./SynergyInfoPopup.js";

export interface CodexSynergyInfoModalDeps {
  readonly modals: ModalHost;
  readonly screen: () => { width: number; height: number };
  readonly levelOf: (synergyId: string) => number;
  readonly openModuleInfo?: (definitionId: string) => void;
  readonly onHighlightPipes?: (synergyId: string) => void;
  readonly onClose?: () => void;
}

export function openCodexSynergyInfo(deps: CodexSynergyInfoModalDeps, synergyId: string): void {
  const data = buildCodexSynergyInfoData(synergyId, deps.levelOf(synergyId));
  if (!data) return;
  const screen = deps.screen();
  deps.modals.openSynergyInfo({
    screenWidth: screen.width,
    screenHeight: screen.height,
    onClose: () => {
      deps.onClose?.();
      deps.modals.closeSynergyInfo();
    },
    onOpenModule: deps.openModuleInfo
      ? (definitionId) => {
          deps.openModuleInfo?.(definitionId);
        }
      : undefined,
    onHighlightPipes: deps.onHighlightPipes,
  });
  deps.modals.updateSynergyInfo(data);
}

export function buildCodexSynergyInfoData(synergyId: string, level: number): SynergyInfoData | undefined {
  const def = synergyDefinitionOf(synergyId);
  if (!def) return undefined;
  return {
    id: def.id,
    name: t(`synergy.${def.id}.name`),
    grade: def.grade,
    level,
    requiredModuleIds: def.requiredModuleIds,
    requiredModuleNames: def.requiredModuleIds.map((id) => t(`module.${id}.name`)),
    currentEffect: effectText(def.effect, level),
    nextEffect: effectText(def.effect, level + 1),
  };
}

function effectText(effect: Parameters<typeof evaluateModuleEffectStats>[0], level: number): string {
  return (
    evaluateModuleEffectStats(effect, level)
      .map((s) => `${t(`moduleStat.${s.key}`)} ${s.value}`)
      .join(" · ") || "-"
  );
}
